import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useEmbedContext } from '@/hooks/useEmbedContext';
import { setAuthToken } from '@/services/api';

interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Sign out and clear token (standalone mode only) */
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isReady, isEmbedded, embedParams } = useEmbedContext();
  const hasToken = Boolean(embedParams.token);

  const [state, setState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    signOut: () => {},
  });

  const signOut = useCallback(() => {
    setAuthToken(null);
    setState((prev) => ({ ...prev, isAuthenticated: false }));
  }, []);

  useEffect(() => {
    if (!isReady) return;

    // Embedded with token → authenticated (backend validates on each API call)
    if (isEmbedded && hasToken) {
      setState({ isLoading: false, isAuthenticated: true, signOut });
      return;
    }

    // Standalone → try dev token endpoint (only exists in development)
    if (!isEmbedded) {
      fetch('/api/dev/token')
        .then((res) => {
          if (!res.ok) throw new Error('not available');
          return res.json();
        })
        .then(({ token }) => {
          setAuthToken(token);
          setState({ isLoading: false, isAuthenticated: true, signOut });
        })
        .catch(() => {
          // Endpoint doesn't exist (production) → block access
          setState({ isLoading: false, isAuthenticated: false, signOut });
        });
      return;
    }

    // Embedded without token → not authenticated
    setState({ isLoading: false, isAuthenticated: false, signOut });
  }, [isReady, isEmbedded, hasToken, signOut]);

  return (
    <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
